import apiClient from "./axiosConfig";

// Live state of the TeleOpd fleet (GET /api/fleet/*). Every number comes from the server SAP runs
// on (pm2 + /proc) or from real HTTP probes; when a source is unavailable the fields are null and
// `reason` says why. Never substitute placeholder values in the UI.

export type FleetSource = "local" | "unavailable";
/** Rollout stage: only "beta" tenants can be changed in SAP until the production release. */
export type TenantStage = "beta" | "locked";
export type FleetTenantStatus = "up" | "degraded" | "down" | "unknown";
/** local = pm2 on this server; remote = tenant runs on another server; unmapped = no pm2 app found. */
export type FleetProcessSource = "local" | "remote" | "unmapped";

/**
 * This SAP process's own share of the server, over the same window as the server's CPU. Its CPU
 * includes the pm2 and df commands SAP runs, but not the pm2 daemon's work answering them.
 */
export interface FleetSelf {
  /** Cores used on average over the window. */
  cpuCores: number | null;
  /** The same as a share of the whole server (all cores). */
  cpuPctOfHost: number | null;
  windowSec: number | null;
  rssMb: number;
  heapUsedMb: number;
  /** Event-loop delay (p99 and max) since the lag statistics last restarted. */
  eventLoopP99Ms: number | null;
  eventLoopMaxMs: number | null;
  eventLoopWindowSec: number;
  uptimeSec: number;
  /** How long the last `pm2 jlist` took. */
  pm2ReadMs: number | null;
}

export interface FleetThresholds {
  /** Probes slower than this count as degraded. */
  slowResponseMs: number;
  /** CPU, memory and disk turn amber at this share, red at the critical one. */
  usageWarnPct: number;
  usageCriticalPct: number;
}

export interface FleetServer {
  /** FLEET_LOCAL_SERVER, e.g. "159.65.145.41"; null when not configured. */
  host: string | null;
  source: FleetSource;
  /** Why metrics are unavailable (null when source is "local"). */
  reason: string | null;
  /** Average busy share over cpuWindowSec (the time since the previous reading). */
  cpuPct: number | null;
  /** Absent from servers deployed before the interval-average change. */
  cpuWindowSec?: number | null;
  /** 1, 5 and 15 minute load averages. */
  load: [number, number, number] | null;
  cores: number | null;
  mem: { totalMb: number; usedPct: number } | null;
  disk: { totalGb: number; usedPct: number } | null;
  uptimeSec: number | null;
  pm2: {
    total: number;
    online: number;
    stopped: number;
    errored: number;
    /** Names of the errored pm2 apps (sent by servers deployed after 2026-10-28). */
    erroredApps?: string[];
  } | null;
}

export interface FleetProcess {
  /** pm2 status: online, stopped, errored, launching… */
  status: string;
  cpuPct: number;
  memMb: number;
  restarts: number;
  uptimeSec: number | null;
  port: number | null;
}

export interface FleetProbe {
  ok: boolean;
  httpStatus: number | null;
  ms: number | null;
  checkedAt: string;
  error: string | null;
}

export interface FleetLink {
  linked: boolean;
  lastSyncAt: string | null;
  appVersion: string | null;
}

export interface FleetTenant {
  tenantId: string;
  /** pm2 app name, the way an instance is recognised (names repeat). */
  slug: string;
  stage: TenantStage;
  name: string;
  uri: string;
  lifecycleStatus: "active" | "disabled" | "not_working";
  deployment: { server: string; app: string; port: number | null } | null;
  status: FleetTenantStatus;
  /** Short human reason for degraded / down / unknown (null when up). */
  statusReason: string | null;
  process: FleetProcess | null;
  processSource: FleetProcessSource;
  probe: FleetProbe | null;
  link: FleetLink;
}

/** Online TeleOpd pm2 apps on this server that SAP has no tenant for. */
export interface FleetUntrackedApp {
  app: string;
  status: string;
  port: number | null;
}

export interface FleetOverview {
  generatedAt: string;
  server: FleetServer;
  /** Every tenant, whatever its lifecycle status or stage (read-only view). */
  tenants: FleetTenant[];
  untracked: FleetUntrackedApp[];
  /** SAP's own usage; null when the server can't be read (absent from older servers). */
  self?: FleetSelf | null;
  /** Dashboard limits in effect (Settings → SAP settings); absent from older servers. */
  thresholds?: FleetThresholds;
  summary: { total: number; up: number; degraded: number; down: number; unknown: number };
}

export interface FleetHostPoint {
  at: string;
  cpuPct: number | null;
  memUsedPct: number | null;
  load1: number | null;
  /** SAP's own share of the server's CPU at this sample (older samples have none). */
  selfCpuPct?: number | null;
}

export interface FleetTenantHistory {
  tenantId: string;
  slug: string;
  samples: number;
  /** Share of samples whose HTTP probe succeeded; null without probe samples. */
  uptimePct: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
}

/** down = HTTP probe failing; process = pm2 status not online; restart = pm2 restart count jumped. */
export type FleetIncidentKind = "down" | "process" | "restart";

export interface FleetIncident {
  tenantId: string;
  slug: string;
  name: string;
  kind: FleetIncidentKind;
  startedAt: string;
  /** null while ongoing. A restart is an instant event: endedAt equals startedAt. */
  endedAt: string | null;
  /** Restarts only: how far the pm2 restart counter jumped (absent from older servers). */
  count?: number;
  detail: string;
}

export interface FleetHistory {
  hours: number;
  from: string;
  to: string;
  sampleMinutes: number;
  /** Oldest first; empty until the sampler has run. */
  hostSeries: FleetHostPoint[];
  tenants: FleetTenantHistory[];
  /** Newest first. */
  incidents: FleetIncident[];
}

export const fleetKeys = {
  all: ["fleet"] as const,
  overview: () => ["fleet", "overview"] as const,
  history: (hours: number) => ["fleet", "history", hours] as const,
};

const BASE = "/api/fleet";

export const fetchFleetOverview = async (): Promise<FleetOverview> => {
  const res = await apiClient.get<FleetOverview>(`${BASE}/overview`);
  return res.data;
};

export const fetchFleetHistory = async (hours = 24): Promise<FleetHistory> => {
  const res = await apiClient.get<FleetHistory>(`${BASE}/history`, { params: { hours } });
  return res.data;
};
